import React, { useState, useEffect } from 'react';
import { Box, Button, Alert, Typography, CircularProgress, Container, Paper, Stack } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { isUserAdmin, addAdmin, checkIfAnyAdminExists } from '../../firebase/firestore';

function AdminInitializer({ onAdminCreated }) {
    const [isAdmin, setIsAdmin] = useState(false);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');
    const [anyAdminExists, setAnyAdminExists] = useState(false);
    const { currentUser } = useAuth();

    // Sprawdź, czy użytkownik jest już administratorem i czy istnieją inni administratorzy
    useEffect(() => {
        const checkStatus = async () => {
            if (currentUser) {
                try {
                    // Sprawdź, czy użytkownik jest administratorem
                    const adminStatus = await isUserAdmin(currentUser.uid);
                    setIsAdmin(adminStatus);

                    // Jeśli nie jest administratorem, sprawdź czy w systemie są już jacyś administratorzy
                    if (!adminStatus) {
                        const adminsExist = await checkIfAnyAdminExists();
                        setAnyAdminExists(adminsExist);
                        if (adminsExist) {
                            setError('Brak uprawnień administratora. W systemie istnieją już administratorzy.');
                        }
                    }
                } catch (err) {
                    console.error("Błąd podczas sprawdzania statusu:", err);
                    setError('Błąd podczas sprawdzania statusu administratora');
                } finally {
                    setLoading(false);
                }
            }
        };

        checkStatus();
    }, [currentUser]);

    // Funkcja do ustawienia użytkownika jako administratora
    const makeAdmin = async () => {
        if (!currentUser) return;

        setLoading(true);
        try {
            await addAdmin(currentUser.uid, {
                email: currentUser.email,
                createdAt: new Date(),
                role: 'superadmin'
            });
            setIsAdmin(true);
            setMessage('Gratulacje! Zostałeś ustawiony jako administrator.');
            // Powiadom komponent nadrzędny o zmianie statusu
            if (onAdminCreated) onAdminCreated();
        } catch (err) {
            console.error("Błąd podczas ustawiania administratora:", err);
            setError('Błąd podczas ustawiania administratora: ' + (err.message || 'Nieznany błąd'));
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Container maxWidth="sm">
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 4, sm: 8 } }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    return (
        <Container maxWidth="sm">
            <Box sx={{
                mt: { xs: 4, sm: 8 },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center'
            }}>
                <Paper
                    elevation={3}
                    sx={{
                        p: { xs: 2, sm: 3 },
                        width: '100%',
                        borderRadius: '0.75rem',
                        backgroundColor: 'rgba(17, 24, 39, 0.95)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(76, 29, 149, 0.2)',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
                    }}
                >
                    <Typography
                        variant="h5"
                        gutterBottom
                        sx={{
                            fontWeight: '700',
                            color: 'white',
                            fontSize: { xs: '1.25rem', sm: '1.5rem' },
                            mb: 2
                        }}
                    >
                        Status Administratora
                    </Typography>

                    <Stack spacing={2}>
                        {isAdmin ? (
                            <Alert severity="success" sx={{ borderRadius: '0.5rem' }}>
                                Jesteś administratorem systemu. Kliknij poniższy przycisk, aby przejść do panelu.
                            </Alert>
                        ) : anyAdminExists ? (
                            <Alert severity="error" sx={{ borderRadius: '0.5rem' }}>
                                Brak uprawnień. Skontaktuj się z istniejącym administratorem, aby uzyskać dostęp.
                            </Alert>
                        ) : (
                            <>
                                <Alert severity="info" sx={{ borderRadius: '0.5rem' }}>
                                    Nie wykryto żadnych administratorów w systemie.
                                </Alert>
                                <Typography
                                    sx={{
                                        color: 'rgb(196, 181, 253)',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    Kliknij poniższy przycisk, aby ustawić swoje konto
                                    ({currentUser?.email}) jako administratora.
                                </Typography>
                                <Button
                                    variant="contained"
                                    onClick={makeAdmin}
                                    disabled={loading}
                                    fullWidth
                                    size="large"
                                    sx={{
                                        mt: 1,
                                        py: 1.25,
                                        backgroundColor: 'rgb(76, 29, 149)',
                                        '&:hover': {
                                            backgroundColor: 'rgb(91, 33, 182)',
                                        },
                                        '&.Mui-disabled': {
                                            backgroundColor: 'rgba(76, 29, 149, 0.3)',
                                            color: 'rgba(255, 255, 255, 0.5)'
                                        },
                                        borderRadius: '0.5rem',
                                        fontWeight: '600'
                                    }}
                                >
                                    {loading ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        'Ustaw jako administratora'
                                    )}
                                </Button>
                            </>
                        )}

                        {message && (
                            <Alert severity="success" sx={{ borderRadius: '0.5rem' }}>
                                {message}
                            </Alert>
                        )}
                        {error && (
                            <Alert severity="error" sx={{ borderRadius: '0.5rem' }}>
                                {error}
                            </Alert>
                        )}
                    </Stack>
                </Paper>
            </Box>
        </Container>
    );
}

export default AdminInitializer;